import React from 'react';
import {
    IEventRenderInfo,
    IEventTimes,
    handleEventClassNames,
} from 'src/components/BookingFlow/modules/TimeTableSelect/fullcalendarFunctions';
import { useState, useCallback, lazy, Suspense } from 'react';
import { setTimeOnDate } from 'src/services/dateTime';
import { IDateInterval, ICalendar } from 'src/entities/calendar';
import { EventInput, EventSourceFunc } from '@fullcalendar/core';
import setMinutes from 'date-fns/fp/setMinutes';
import setHours from 'date-fns/fp/setHours';
import addMinutes from 'date-fns/fp/addMinutes';
import getMinutes from 'date-fns/fp/getMinutes';
import subDays from 'date-fns/subDays';
import { getCalendarFreeIntervals } from 'src/services/api/calendar';
import { IService } from 'src/entities/service';
import { useIntl, IntlShape } from 'react-intl';
import getDay from 'date-fns/getDay';
import Placeholder from 'src/components/Placeholder/Placeholder';
import { ReadonlyInfo } from 'src/components/ReadonlyInfo/ReadonlyInfo';
import PersonCount from './PersonCount/PersonCount';
import { IPendingReservation } from 'src/entities/reservation';

const Timetable = lazy(() => import(/* webpackChunkName: "Timetable" */ './Timetable'));

/* assuming 1rem = 11px */
const mobileBreakPoints: Record<number, string> = {
    6: '91rem',
    4: '82rem',
    3: '91rem',
    2: '56rem',
    1: '56rem',
};

const getMobileBreakPoint = (cellLength: number) => {
    const rootFontSizeScale = 11 / 16;
    return mobileBreakPoints[getCellCount(cellLength)].replace(/\d+/g, (value) =>
        String(+value * rootFontSizeScale)
    );
};

export const getSlotLength = (resolutionInMinutes: number) => {
    if (resolutionInMinutes < 10) {
        return 30;
    }

    if (resolutionInMinutes > 30) {
        return resolutionInMinutes;
    }

    return 60;
};

const getCellCount = (cellLength: number) => {
    const slotLength = getSlotLength(cellLength);
    return slotLength / cellLength;
};

type IGroupedTimes = Record<string, Date[]>;

const toList = (map: IGroupedTimes) =>
    Object.entries(map).map<[Date, Date[]]>(([key, value]) => [new Date(key), value]);

const toEventTimes = (dateInterval: IDateInterval): IEventTimes => {
    const { Date, Intervals } = dateInterval;
    return { [Date]: Intervals };
};

const indexTime = (date: Date, cellSize: number, slotSize: 60 | 30) => {
    const minutes = getMinutes(date) % slotSize;
    const index = Math.floor(minutes / cellSize);
    return index;
};

const floorToHour = (dateTime: Date) => setMinutes(0, dateTime);
const floorToHalfHour = (dateTime: Date) =>
    getMinutes(dateTime) < 30 ? setMinutes(0, dateTime) : setMinutes(30, dateTime);
const addTime = (time: Date, key: string, map: IGroupedTimes) => {
    if (!(key in map)) {
        map[key] = [];
    }
    map[key].push(time);
};

const groupBy = (dates: string[], step: number): Array<[Date, Date[]]> => {
    if (step > 30) {
        return dates.map((ds) => new Date(ds)).map((d) => [d, [d]]);
    }

    const grouped = dates
        .map((s) => new Date(s))
        .reduce<IGroupedTimes>((map, dateTime) => {
            const key = (
                step < 10 ? floorToHalfHour(dateTime) : floorToHour(dateTime)
            ).toISOString();

            addTime(dateTime, key, map);

            return map;
        }, {});

    return toList(grouped);
};

const mapResponse = (dateIntervals: IDateInterval[], step: number): EventInput[] => {
    const slotLength = getSlotLength(step);

    const startTimes = dateIntervals
        .map(({ Date, Intervals }) => {
            return Intervals.map((interval) => setTimeOnDate(Date, interval.Start));
        })
        .flat();

    const groupedTimes = groupBy(startTimes, step);

    return groupedTimes.map(([endPoint, times]) => {
        return {
            title: endPoint.toString(),
            start: endPoint.toISOString(),
            end: addMinutes(slotLength, endPoint).toISOString(),
            extendedProps: {
                times,
            },
        };
    });
};

interface IAppointmentCalendarProps {
    calendar: ICalendar;
    service: IService;
    personCount: number;
    firstFreeSlot: string;
    querySelectedDate?: string | undefined;
    onDateTimeSelect?: (dateTime: string) => void;
    onPersonCountChange: (newPersonCount: number) => void;
    pendingReservations: IPendingReservation[];
}

const formatTime = (time: Date, intl: IntlShape) => {
    const formattedTime = intl.formatTime(time);
    if (intl.locale !== 'en') {
        return formattedTime;
    }
    const [numerics, period] = formattedTime.split(' ');
    return numerics + (period === 'AM' ? 'ᴬᴹ' : 'ᴾᴹ');
};

const AppointmentCalendar: React.FunctionComponent<IAppointmentCalendarProps> = ({
    calendar,
    service,
    personCount,
    firstFreeSlot,
    querySelectedDate,
    onDateTimeSelect,
    onPersonCountChange,
    pendingReservations,
}) => {
    const [eventTimes, setEventTimes] = useState<IEventTimes>({});

    const intl = useIntl();

    const userViewSlotMinutes: number = service.Duration === 0 ? 5 : calendar.UserViewSlotMinutes;

    const handleEventRender = (
        arg: IEventRenderInfo & {
            currentWeekDay: number | false;
        }
    ) => {
        const times: Date[] = arg.event.extendedProps.times!;
        const { currentWeekDay, view } = arg;

        const day = getDay(times[0]);
        if (currentWeekDay !== false && day !== currentWeekDay) {
            return null;
        }

        const slotSize = getSlotLength(userViewSlotMinutes);
        const cellCount = getCellCount(userViewSlotMinutes);
        const cells: Array<Date | null> = Array.from({ length: cellCount }, () => null);

        if (cellCount === 1) {
            cells[0] = times[0];
        } else {
            times.forEach((time) => {
                const index = indexTime(time, userViewSlotMinutes, slotSize as 30 | 60);
                cells[index] = time;
            });
        }

        const isInteractive = !calendar.Readonly;

        const slot = (
            <ol className="appointment-calendar__slot" data-cells={cellCount}>
                {cells.map((time, i) =>
                    time ? (
                        <li className={'appointment-calendar__cell'} key={time.toISOString()}>
                            {isInteractive ? (
                                <button
                                    className={'appointment-calendar__cell-button'}
                                    onClick={() => onDateTimeSelect?.(time.toISOString())}
                                >
                                    <time>{formatTime(time, intl)}</time>
                                </button>
                            ) : (
                                <time>{formatTime(time, intl)}</time>
                            )}
                        </li>
                    ) : (
                        <li key={i} className="appointment-calendar__cell--empty" />
                    )
                )}
            </ol>
        );

        const isListView = view.type.includes('list');

        return isListView ? <div style={{ gridColumn: '1 / -1' }}>{slot}</div> : slot;
    };

    const eventClassNames = (arg: IEventRenderInfo) =>
        handleEventClassNames({
            ...arg,
            minTimeReserve: calendar.MinTimeReserve,
            firstFreeSlot
        });

    const getEventTimes: EventSourceFunc = useCallback(
        ({ start, end: exclusiveEnd }, successCallback, failureCallback) => {
            const end = subDays(new Date(exclusiveEnd), 1);
            getCalendarFreeIntervals({
                calendarId: calendar.Id,
                serviceId: service.Id,
                personCount,
                startDate: setHours(12, start).toISOString(),
                endDate: setHours(12, end).toISOString(),
                pendingReservations,
            })
                .then((dateIntervals) => {
                    if (dateIntervals) {
                        setEventTimes(
                            dateIntervals
                                .map(({ Date, Intervals }) => {
                                    return {
                                        Date,
                                        Intervals: Intervals.map((interval) => ({
                                            ...interval,
                                            Start: setTimeOnDate(Date, interval.Start),
                                        })),
                                    };
                                })
                                .map(toEventTimes)
                                .reduce((acc, entry) => ({ ...acc, ...entry }))
                        );
                        const mapped =  mapResponse(dateIntervals, userViewSlotMinutes);
                        return mapped;
                    }
                    return [];
                })
                .then(successCallback)
                .catch(failureCallback);
        },
        [personCount]
    );

    return (
        <div className="appointment-calendar">
            <div className="appointment-calendar__text">
                {calendar.Readonly && <ReadonlyInfo phoneNumber={calendar.PhoneNumber} className='appointment-calendar__readonly' />}
            </div>
            <PersonCount
                    count={personCount}
                    onChange={onPersonCountChange}
                    maxCount={calendar.ConcurrentNum}
                    className='appointment-calendar__person-count'
                />
            <Suspense fallback={<Placeholder />}>
                <Timetable
                    {...{
                        getEventTimes,
                        handleEventRender,
                        handleEventClassNames: eventClassNames,
                        eventTimes,
                        calendar,
                        slotDuration: getSlotLength(userViewSlotMinutes),
                        firstFreeSlot,
                        mobileBreakPoint: getMobileBreakPoint(userViewSlotMinutes),
                        showViewSelectorOnMobile: true,
                        querySelectedDate,
                        userViewSlotMinutes,
                    }}
                />
            </Suspense>
        </div>
    );
};

export default AppointmentCalendar;